<template>
  <div v-if="selectedVariant" class="bundle-tile flex-inline">
    <slot />
    <a
      :href="localePath(product?.url || '')"
      :alt="`View ${selectedVariant.display_title} Product Page`"
      class="tile-img-wrapper"
    >
      <!-- @todo @test -- next sprint -->
      <!-- <img
        :src="getImage"
        :alt="useImgAlt(selectedVariant.display_title)"
        class="tile-img"
        loading="lazy"
        height="96"
        width="96"
      /> -->
      <CloudinaryImage
        :public-id="selectedVariant.image?.[0]?.public_id"
        :options="{ width: 96, height: 96, quality: 'auto:eco' }"
        :alt="useImgAlt(selectedVariant.display_title)"
        :image-classes-override="['tile-img']"
      />
    </a>
    <div class="flex-col prod-data">
      <div class="prod-title ff-text-500">
        {{ isCurrentProduct ? 'Current Item: ' : ''
        }}{{ selectedVariant.display_title + (minPurchaseQuantity > 1 ? ` (${minPurchaseQuantity})` : '') }}
        <span v-if="selectedVariant && selectedVariant.backorder_message" class="backorder-message">{{
          selectedVariant.backorder_message
        }}</span>
      </div>
      <YotpoStarRating :product-id="baseProduct.id" :yotpo-key="yotpoKey" />
      <p
        v-if="!isCurrentProduct && selectedVariant.short_description"
        class="short-desc"
        v-html="selectedVariant.short_description"
      />
      <p v-else-if="!isCurrentProduct && product.description" class="short-desc" v-html="product.description" />
      <!-- eslint-disable-line vue/no-v-html -->
      <PriceSection :sku="selectedVariant?.bc_variant?.sku" :quantity="minPurchaseQuantity" :is-pdp="true" />
      <ProductOptions
        v-if="productVariations?.length"
        :sku="baseProduct?.sku"
        :product-variations="productVariations"
        @product-options:update="updateOption"
        @modifiers-changed="setProductModifiers"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductV2 } from '@solo-stove/types/contentstack/stove/product';
import { useRootStore } from '~/store';
const localePath = useLocalePath();
const rootStore = useRootStore();
const emit = defineEmits(['bundle-tile-option-selected']);
const yotpoKey = rootStore.yotpoAppKey;

const props = defineProps({
  product: {
    type: Object as () => ProductV2,
    required: true,
  },
  isCurrentProduct: {
    type: Boolean,
    default: false,
  },
  perItemBundleSavings: {
    type: Number,
    default: 0,
  },
});

const state = reactive({
  bcVariant: {} as any,
  productModifiers: [] as any[],
  imgSize: 500,
});

const baseProduct = computed<any>(() => props.product?.product_offerings?.bc_primary_product?.product);
const productVariations = computed(() => {
  return props.product?.product_offerings?.product_variations;
});
const minPurchaseQuantity = computed(() => props.product?.product_details?.min_quantity || 1);

const selectedVariant = computed<any>(() => {
  let locatedIndex = 0;
  productVariations?.value.find((option: any, i: number) => {
    if (option?.bc_variant?.id === state.bcVariant?.id) {
      locatedIndex = i;
      return true;
    }
    return false;
  });

  return productVariations?.value?.[locatedIndex];
});

function updateOption(updatedVariant: any) {
  state.bcVariant = updatedVariant;
  emit('bundle-tile-option-selected', updatedVariant);
}
function setProductModifiers(modData: any) {
  state.productModifiers = modData as any;
}
</script>

<style lang="scss">
.bundle-tile {
  .short-desc,
  .short-desc p,
  .short-desc span,
  .short-desc a {
    font-size: 0.75rem;
    margin-bottom: 8px;
  }
  .yotpo.bottomLine {
    .rating-star {
      font-size: 1rem;
    }
    .text-m {
      font-size: 0.6875rem;
      pointer-events: none;
      text-decoration: none !important;
      color: $color-neutral-cool-900 !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.bundle-tile {
  display: flex;
  padding: 0.5rem 1rem;
  border: 1px solid $color-neutral-cool-300;
  border-radius: 4px;
  + .bundle-tile {
    margin-top: 1rem;
  }
  .prod-data {
    width: -webkit-fill-available;
  }
  .tile-img {
    height: 96px;
    width: 96px;
    margin: 0 1rem;
    @include local-mixins.tablet_and_mobile {
      height: 64px;
      width: 64px;
      margin: 0 0.75rem 0 0;
    }
  }
  @include local-mixins.mobile {
    display: grid;
    gap: 6px;
    grid-template-areas:
      'check data'
      'img data';
    grid-template-rows: 0.5fr 2fr;
    .prod-data {
      grid-area: data;
    }
    .checkradio-container {
      grid-area: check;
      margin-bottom: 4px;
    }
    .tile-img-wrapper {
      grid-area: img;
      display: flex;
      align-self: flex-start;
      .tile-img {
        margin-right: 0;
      }
    }
  }
  :deep(.price-container) {
    .current-price {
      font-size: 0.875rem;
    }
    .orig-price {
      margin-left: 8px;
    }
    .orig-price .strikethrough {
      font-size: 0.875rem;
    }
    .orig-price .msrp-price {
      font-size: 0.75rem !important;
    }
    .savings-cta {
      display: none;
    }
    @include local-mixins.tablet_and_mobile {
      .orig-price {
        .strikethrough,
        .msrp-price {
          font-size: 0.875rem;
        }
      }
    }
  }
  :deep(.options-container) {
    margin-bottom: 0;
    &.multi-option {
      .options-wrapper {
        margin-bottom: 0.5rem;
      }
    }
    .selected-title {
      display: none;
    }
    .color-swatch {
      height: 20px;
      width: 20px;
    }
    .options-wrapper {
      margin-top: 0;
    }
    .variant-wrapper {
      width: 100%;

      .option {
        padding: 8px;
        font-size: 0.75rem;
      }
      &.opt-button:not(.selected) {
        border: $color-neutral-cool-200 1px solid;
        border-radius: 3px;
        &:hover {
          border: $color-neutral-black 1px solid;
        }
      }
      &.selected {
        border: $color-primary-500 2px solid;
        border-radius: 3px;
      }
      .select-expanded {
        @include local-mixins.tablet_and_mobile {
          min-width: revert;
          width: 100%;
        }
      }
    }
  }
  .prod-title {
    font-size: 1rem;
  }
  .backorder-message {
    font-weight: 500;
    color: $color-primary-600;
    font-size: 0.875rem;
    margin-left: 5px;
  }
}
</style>
